import React, { useCallback, useEffect, useState } from 'react';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AlertModal from '../components/AlertModal';
import CompetenceMonthField from '../components/CompetenceMonthField';
import ConfirmModal from '../components/ConfirmModal';
import PageContainer from '../components/ui/PageContainer';
import StatusChip from '../components/ui/StatusChip';
import api from '../services/api';
import { useClientPage } from '../components/useClientPage';
import PaymentsPage from './PaymentsPage';

type TabId =
  | 'pagamentos'
  | 'lancamentos'
  | 'cobrancas'
  | 'folha'
  | 'ciclos'
  | 'relatorios'
  | 'mensal'
  | 'bandas'
  | 'cadastros';

const NAV: { id: TabId; label: string }[] = [
  { id: 'pagamentos', label: 'Pagamentos' },
  { id: 'lancamentos', label: 'Lançamentos' },
  { id: 'cobrancas', label: 'Cobranças' },
  { id: 'folha', label: 'Folha' },
  { id: 'ciclos', label: 'Ciclos' },
  { id: 'relatorios', label: 'Relatórios' },
  { id: 'mensal', label: 'Planilha mensal' },
  { id: 'bandas', label: 'Bandas' },
  { id: 'cadastros', label: 'Cadastros' },
];

const FinanceWorkspace: React.FC = () => {
  const [tab, setTab] = useState<TabId>('pagamentos');

  return (
    <PageContainer
      maxWidth="xl"
      icon={<AccountBalanceWalletOutlinedIcon />}
      title="Financeiro"
      subtitle="Pagamentos, lançamentos, ciclos e relatórios do financeiro."
    >
      <Tabs
        value={tab}
        onChange={(_event, value: TabId) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        {NAV.map((item) => (
          <Tab key={item.id} value={item.id} label={item.label} />
        ))}
      </Tabs>
      {tab === 'pagamentos' ? <PaymentsPage embedded /> : null}
      {tab === 'lancamentos' ? <LancamentosPanel /> : null}
      {tab === 'cobrancas' ? <CobrancasPanel /> : null}
      {tab === 'folha' ? <FolhaPanel /> : null}
      {tab === 'ciclos' ? <CiclosPanel /> : null}
      {tab === 'relatorios' ? <RelatoriosPanel /> : null}
      {tab === 'mensal' ? <MensalPanel /> : null}
      {tab === 'bandas' ? <BandasPanel /> : null}
      {tab === 'cadastros' ? <CadastrosPanel /> : null}
    </PageContainer>
  );
};

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const formRowSx = { alignItems: { xs: 'stretch', md: 'flex-end' }, flexWrap: 'wrap' } as const;
const fieldButtonSx = { height: 40, minWidth: 112, flexShrink: 0, px: 2 } as const;

function cents(raw: string): number | null {
  const text = raw.trim();
  if (!text) return 0;
  let normalized = text.replace(/\s/g, '');
  if (normalized.includes(',')) normalized = normalized.replace(/\./g, '').replace(',', '.');
  if (normalized.startsWith('-')) {
    const abs = normalized.slice(1);
    if (!/^\d+(\.\d{1,2})?$/.test(abs)) return null;
    return -Math.round(Number(abs) * 100);
  }
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  return Math.round(Number(normalized) * 100);
}

function money(centsValue: number): string {
  return brl.format(centsValue / 100);
}

function PanelError({ message, onClose }: { message: string; onClose: () => void }) {
  return <AlertModal isOpen={Boolean(message)} onClose={onClose} message={message} type="error" />;
}

interface Overview {
  summary: { totalIncome: number; totalExpense: number; balance: number; transactionsCount: number };
  transactions: Array<{
    id: string;
    description: string;
    amount: number | string;
    transactionDate: string;
    displayType?: string;
    type: string;
    approvalStatus?: string;
    notes?: string | null;
    attachmentOriginalName?: string | null;
    account?: { id?: string; name?: string } | null;
    source?: { name?: string } | null;
    category?: { id?: string; name?: string } | null;
    cycle?: { id?: string; name?: string } | null;
    cycleId?: string | null;
    accountId?: string | null;
    sourceId?: string | null;
    categoryId?: string | null;
  }>;
  accounts: Array<{ id: string; name: string; type: string; active: boolean }>;
  banks: Array<{ id: string; name: string; code?: string | null; active: boolean }>;
  categories: Array<{ id: string; name: string; type: string; level: string; active: boolean; parentId?: string | null }>;
  sources?: Array<{ id: string; name: string; type: string; active: boolean }>;
  tags?: Array<{ id: string; name: string; active: boolean }>;
  keywords?: Array<{ id: string; term: string; scope?: string }>;
  cycles?: Array<{ id: string; name: string; active?: boolean }>;
}

const LancamentosPanel: React.FC = () => {
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [kind, setKind] = useState('expense');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [accountId, setAccountId] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [cycleId, setCycleId] = useState('');
  const [notes, setNotes] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const txPage = useClientPage(data?.transactions ?? []);

  const removeTx = async (id: string) => {
    try {
      await api.delete(`/finance/transactions/${id}`);
      await load();
    } catch {
      setAlert('Não foi possível excluir o lançamento.');
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<Overview>('/finance/overview');
      setData(res.data);
      setAccountId((current) => current || res.data.accounts[0]?.id || '');
    } catch {
      setAlert('Não foi possível carregar os lançamentos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    const value = cents(amount);
    if (!description.trim() || value == null || value < 0) {
      setAlert('Informe descrição e valor.');
      return;
    }
    try {
      const payload = {
        type: kind,
        description: description.trim(),
        amount: String(value / 100),
        transactionDate: date,
        accountId: accountId || '',
        sourceId: sourceId || '',
        categoryId: categoryId || '',
        cycleId: cycleId || '',
        notes: notes.trim(),
      };
      if (file) {
        const body = new FormData();
        Object.entries(payload).forEach(([key, item]) => {
          if (item) body.append(key, item);
        });
        body.append('attachment', file);
        await api.post('/finance/transactions', body);
      } else {
        await api.post('/finance/transactions', {
          ...payload,
          amount: value / 100,
          accountId: accountId || undefined,
          sourceId: sourceId || undefined,
          categoryId: categoryId || undefined,
          cycleId: cycleId || undefined,
          notes: notes.trim() || undefined,
        });
      }
      setDescription('');
      setAmount('');
      setNotes('');
      setFile(null);
      setEditingId(null);
      await load();
    } catch {
      setAlert('Não foi possível lançar.');
    }
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const value = cents(amount);
    if (!description.trim() || value == null || value < 0) {
      setAlert('Informe descrição e valor.');
      return;
    }
    try {
      await api.patch(`/finance/transactions/${editingId}`, {
        type: kind,
        description: description.trim(),
        amount: value / 100,
        transactionDate: date,
        accountId: accountId || undefined,
        sourceId: sourceId || undefined,
        categoryId: categoryId || undefined,
        cycleId: cycleId || undefined,
        notes: notes.trim() || undefined,
      });
      setEditingId(null);
      setDescription('');
      setAmount('');
      setNotes('');
      await load();
    } catch {
      setAlert('Não foi possível atualizar o lançamento.');
    }
  };

  const decide = async (id: string, decision: 'approve' | 'reject') => {
    try {
      await api.post(`/finance/transactions/${id}/${decision}`);
      await load();
    } catch {
      setAlert('Não foi possível atualizar a aprovação.');
    }
  };

  const downloadAttachment = async (id: string, name: string) => {
    try {
      const res = await api.get(`/finance/transactions/${id}/attachment`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = name || 'comprovante';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch {
      setAlert('Não foi possível baixar o comprovante.');
    }
  };

  if (loading || !data) return <CircularProgress />;

  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Entradas</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{brl.format(Number(data.summary.totalIncome))}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Saídas</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{brl.format(Number(data.summary.totalExpense))}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Saldo</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{brl.format(Number(data.summary.balance))}</Typography>
          </Paper>
        </Grid>
      </Grid>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Novo lançamento</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={formRowSx}>
          <TextField select size="small" label="Tipo" value={kind} onChange={(e) => setKind(e.target.value)} slotProps={{ select: { native: true } }} sx={{ minWidth: 140 }}>
            <option value="expense">Saída</option>
            <option value="income">Entrada</option>
            <option value="tithe">Dízimo</option>
          </TextField>
          <TextField size="small" label="Descrição" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth />
          <TextField size="small" label="Valor" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" sx={{ width: 140 }} />
          <TextField size="small" type="date" label="Data" value={date} onChange={(e) => setDate(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField select size="small" label="Conta" value={accountId} onChange={(e) => setAccountId(e.target.value)} slotProps={{ select: { native: true } }} sx={{ minWidth: 180 }}>
            <option value="">Sem conta</option>
            {data.accounts.map((account) => (
              <option key={account.id} value={account.id}>{account.name}</option>
            ))}
          </TextField>
          <TextField select size="small" label="Fonte" value={sourceId} onChange={(e) => setSourceId(e.target.value)} slotProps={{ select: { native: true } }} sx={{ minWidth: 160 }}>
            <option value="">Sem fonte</option>
            {(data.sources || []).map((source) => (
              <option key={source.id} value={source.id}>{source.name}</option>
            ))}
          </TextField>
          <TextField select size="small" label="Categoria" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} slotProps={{ select: { native: true } }} sx={{ minWidth: 160 }}>
            <option value="">Sem categoria</option>
            {data.categories.map((category) => (
              <option key={category.id} value={category.id}>{category.name}</option>
            ))}
          </TextField>
          <TextField select size="small" label="Ciclo" value={cycleId} onChange={(e) => setCycleId(e.target.value)} slotProps={{ select: { native: true } }} sx={{ minWidth: 160 }}>
            <option value="">Sem ciclo</option>
            {(data.cycles || []).map((cycle) => (
              <option key={cycle.id} value={cycle.id}>{cycle.name}</option>
            ))}
          </TextField>
          <TextField size="small" label="Observação" value={notes} onChange={(e) => setNotes(e.target.value)} sx={{ minWidth: 180 }} />
          <Button variant="outlined" component="label" sx={fieldButtonSx}>
            Comprovante
            <input hidden type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </Button>
          <Button variant="contained" onClick={() => void (editingId ? saveEdit() : create())} sx={fieldButtonSx}>
            {editingId ? 'Atualizar' : 'Lançar'}
          </Button>
        </Stack>
      </Paper>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Data</TableCell>
            <TableCell>Descrição</TableCell>
            <TableCell>Conta</TableCell>
            <TableCell>Categoria</TableCell>
            <TableCell>Ciclo</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell align="right">Valor</TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {txPage.slice.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell>{String(row.transactionDate).slice(0, 10).split('-').reverse().join('/')}</TableCell>
              <TableCell>{row.description}</TableCell>
              <TableCell>{row.account?.name || '—'}</TableCell>
              <TableCell>{row.category?.name || '—'}</TableCell>
              <TableCell>{row.cycle?.name || '—'}</TableCell>
              <TableCell>{row.approvalStatus === 'pending' ? 'Pendente' : row.displayType || row.type}</TableCell>
              <TableCell align="right">{brl.format(Number(row.amount))}</TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                  {row.approvalStatus === 'pending' ? (
                    <>
                      <Button size="small" onClick={() => void decide(row.id, 'approve')}>Aprovar</Button>
                      <Button size="small" color="inherit" onClick={() => void decide(row.id, 'reject')}>Recusar</Button>
                    </>
                  ) : null}
                  {row.attachmentOriginalName ? (
                    <Button size="small" onClick={() => void downloadAttachment(row.id, row.attachmentOriginalName || 'comprovante')}>Anexo</Button>
                  ) : null}
                  <Button
                    size="small"
                    onClick={() => {
                      setEditingId(row.id);
                      setDescription(row.description);
                      setAmount(String(row.amount).replace('.', ','));
                      setKind(row.type);
                      setDate(String(row.transactionDate).slice(0, 10));
                      setAccountId(row.accountId || row.account?.id || '');
                      setSourceId(row.sourceId || '');
                      setCategoryId(row.categoryId || row.category?.id || '');
                      setCycleId(row.cycleId || row.cycle?.id || '');
                      setNotes(row.notes || '');
                    }}
                  >
                    Editar
                  </Button>
                  <IconButton aria-label={`Excluir lançamento ${row.description}`} size="small" onClick={() => void removeTx(row.id)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {txPage.pager}
      <PanelError message={alert} onClose={() => setAlert('')} />
    </Stack>
  );
};

const CadastrosPanel: React.FC = () => {
  const [data, setData] = useState<Overview | null>(null);
  const [alert, setAlert] = useState('');
  const [accountName, setAccountName] = useState('');
  const [bankName, setBankName] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [sourceName, setSourceName] = useState('');
  const [sourceType, setSourceType] = useState('expense');
  const [tagName, setTagName] = useState('');
  const [keywordTerm, setKeywordTerm] = useState('');
  const [pendingDelete, setPendingDelete] = useState<{
    kind: 'accounts' | 'banks' | 'categories' | 'sources' | 'tags' | 'keywords';
    id: string;
    name: string;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const accountsPage = useClientPage(data?.accounts ?? []);
  const banksPage = useClientPage(data?.banks ?? []);
  const categoriesPage = useClientPage(data?.categories ?? []);
  const sourcesPage = useClientPage(data?.sources ?? []);
  const tagsPage = useClientPage(data?.tags ?? []);
  const keywordsPage = useClientPage(data?.keywords ?? []);

  const load = useCallback(async () => {
    try {
      const res = await api.get<Overview>('/finance/overview');
      setData(res.data);
    } catch {
      setAlert('Não foi possível carregar os cadastros.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const addAccount = async () => {
    if (!accountName.trim()) return;
    try {
      await api.post('/finance/accounts', { name: accountName.trim(), type: 'bank' });
      setAccountName('');
      await load();
    } catch {
      setAlert('Não foi possível criar a conta.');
    }
  };

  const addBank = async () => {
    if (!bankName.trim()) return;
    try {
      await api.post('/finance/banks', { name: bankName.trim() });
      setBankName('');
      await load();
    } catch {
      setAlert('Não foi possível criar o banco.');
    }
  };

  const addCategory = async () => {
    if (!categoryName.trim()) return;
    try {
      await api.post('/finance/categories', { name: categoryName.trim(), type: 'expense', level: 'macro' });
      setCategoryName('');
      await load();
    } catch {
      setAlert('Não foi possível criar a categoria.');
    }
  };

  const addSource = async () => {
    if (!sourceName.trim()) return;
    try {
      await api.post('/finance/sources', { name: sourceName.trim(), type: sourceType });
      setSourceName('');
      await load();
    } catch {
      setAlert('Não foi possível criar a fonte.');
    }
  };

  const addTag = async () => {
    if (!tagName.trim()) return;
    try {
      await api.post('/finance/tags', { name: tagName.trim() });
      setTagName('');
      await load();
    } catch {
      setAlert('Não foi possível criar a tag.');
    }
  };

  const addKeyword = async () => {
    if (!keywordTerm.trim()) return;
    try {
      await api.post('/finance/keywords', { term: keywordTerm.trim() });
      setKeywordTerm('');
      await load();
    } catch {
      setAlert('Não foi possível criar a palavra-chave.');
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/finance/${pendingDelete.kind}/${pendingDelete.id}`);
      setPendingDelete(null);
      await load();
    } catch {
      setAlert('Não foi possível excluir. O item pode estar em uso em algum lançamento.');
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  if (!data) return <CircularProgress />;

  const columns: Array<{
    title: string;
    value: string;
    onChange: (value: string) => void;
    onSave: () => void;
    items: Array<{ id: string; name: string; label: string }>;
    onDelete: (id: string, name: string) => void;
    pager: React.ReactNode;
  }> = [
    {
      title: 'Contas',
      value: accountName,
      onChange: setAccountName,
      onSave: () => void addAccount(),
      items: accountsPage.slice.map((item) => ({
        id: item.id,
        name: item.name,
        label: item.active ? item.name : `${item.name} (inativa)`,
      })),
      onDelete: (id, name) => setPendingDelete({ kind: 'accounts', id, name }),
      pager: accountsPage.pager,
    },
    {
      title: 'Bancos',
      value: bankName,
      onChange: setBankName,
      onSave: () => void addBank(),
      items: banksPage.slice.map((item) => ({ id: item.id, name: item.name, label: item.name })),
      onDelete: (id, name) => setPendingDelete({ kind: 'banks', id, name }),
      pager: banksPage.pager,
    },
    {
      title: 'Categorias',
      value: categoryName,
      onChange: setCategoryName,
      onSave: () => void addCategory(),
      items: categoriesPage.slice.map((item) => ({
        id: item.id,
        name: item.name,
        label: `${item.name} · ${item.level}`,
      })),
      onDelete: (id, name) => setPendingDelete({ kind: 'categories', id, name }),
      pager: categoriesPage.pager,
    },
  ];

  return (
    <>
    <Grid container spacing={2}>
      {columns.map((column) => (
        <Grid key={column.title} size={{ xs: 12, md: 4 }}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography sx={{ fontWeight: 800, mb: 2 }}>{column.title}</Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-end', mb: 2 }}>
              <TextField
                size="small"
                label="Nome"
                value={column.value}
                onChange={(e) => column.onChange(e.target.value)}
                fullWidth
                onKeyDown={(e) => {
                  if (e.key === 'Enter') column.onSave();
                }}
              />
              <Button
                variant="contained"
                onClick={column.onSave}
                sx={fieldButtonSx}
              >
                Salvar
              </Button>
            </Stack>
            <Stack divider={<Divider flexItem />} spacing={0}>
              {column.items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nenhum item nesta página.</Typography>
              ) : (
                column.items.map((item) => (
                  <Stack key={item.id} direction="row" spacing={1} sx={{ alignItems: 'center', py: 0.75 }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>{item.label}</Typography>
                    <IconButton aria-label={`Excluir ${item.name}`} size="small" onClick={() => column.onDelete(item.id, item.name)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))
              )}
            </Stack>
            {column.pager}
          </Paper>
        </Grid>
      ))}
    </Grid>
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
          <Typography sx={{ fontWeight: 800, mb: 2 }}>Fontes</Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-end', mb: 2 }}>
            <TextField size="small" label="Nome" value={sourceName} onChange={(e) => setSourceName(e.target.value)} fullWidth />
            <TextField select size="small" label="Tipo" value={sourceType} onChange={(e) => setSourceType(e.target.value)} slotProps={{ select: { native: true } }} sx={{ minWidth: 120 }}>
              <option value="expense">Saída</option>
              <option value="income">Entrada</option>
            </TextField>
            <Button variant="contained" onClick={() => void addSource()} sx={fieldButtonSx}>Salvar</Button>
          </Stack>
          {sourcesPage.slice.map((item) => (
            <Stack key={item.id} direction="row" sx={{ alignItems: 'center', py: 0.5 }}>
              <Typography variant="body2" sx={{ flex: 1 }}>{item.name}</Typography>
              <IconButton aria-label={`Excluir fonte ${item.name}`} size="small" onClick={() => setPendingDelete({ kind: 'sources', id: item.id, name: item.name })}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          {sourcesPage.pager}
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
          <Typography sx={{ fontWeight: 800, mb: 2 }}>Tags</Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-end', mb: 2 }}>
            <TextField size="small" label="Nome" value={tagName} onChange={(e) => setTagName(e.target.value)} fullWidth />
            <Button variant="contained" onClick={() => void addTag()} sx={fieldButtonSx}>Salvar</Button>
          </Stack>
          {tagsPage.slice.map((item) => (
            <Stack key={item.id} direction="row" sx={{ alignItems: 'center', py: 0.5 }}>
              <Typography variant="body2" sx={{ flex: 1 }}>{item.name}</Typography>
              <IconButton aria-label={`Excluir tag ${item.name}`} size="small" onClick={() => setPendingDelete({ kind: 'tags', id: item.id, name: item.name })}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          {tagsPage.pager}
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
          <Typography sx={{ fontWeight: 800, mb: 2 }}>Palavras-chave</Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-end', mb: 2 }}>
            <TextField size="small" label="Termo" value={keywordTerm} onChange={(e) => setKeywordTerm(e.target.value)} fullWidth />
            <Button variant="contained" onClick={() => void addKeyword()} sx={fieldButtonSx}>Salvar</Button>
          </Stack>
          {keywordsPage.slice.map((item) => (
            <Stack key={item.id} direction="row" sx={{ alignItems: 'center', py: 0.5 }}>
              <Typography variant="body2" sx={{ flex: 1 }}>{item.term}</Typography>
              <IconButton aria-label={`Excluir ${item.term}`} size="small" onClick={() => setPendingDelete({ kind: 'keywords', id: item.id, name: item.term })}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          {keywordsPage.pager}
        </Paper>
      </Grid>
    </Grid>
      <ConfirmModal
        isOpen={Boolean(pendingDelete)}
        title="Excluir cadastro"
        message={pendingDelete ? `Excluir “${pendingDelete.name}”?` : ''}
        confirmLabel="Excluir"
        tone="danger"
        confirmDisabled={deleting}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setPendingDelete(null)}
      />
      <PanelError message={alert} onClose={() => setAlert('')} />
    </>
  );
};

interface Cobranca {
  id: string;
  descricao: string;
  clienteNome: string;
  valorCents: number;
  status: string;
  linkUrl?: string | null;
}

const CobrancasPanel: React.FC = () => {
  const [rows, setRows] = useState<Cobranca[]>([]);
  const [alert, setAlert] = useState('');
  const [form, setForm] = useState({ descricao: '', clienteNome: '', clienteEmail: '', valor: '' });
  const page = useClientPage(rows);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ items?: Cobranca[] } | Cobranca[]>('/cobrancas', { params: { page: 1, limit: 100 } });
      const payload = res.data;
      setRows(Array.isArray(payload) ? payload : payload.items || []);
    } catch {
      setAlert('Não foi possível carregar as cobranças.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    const valorCents = cents(form.valor);
    if (!form.descricao || !form.clienteNome || !form.clienteEmail || !valorCents || valorCents < 1) {
      setAlert('Preencha descrição, cliente, e-mail e valor.');
      return;
    }
    try {
      await api.post('/cobrancas', {
        descricao: form.descricao,
        clienteNome: form.clienteNome,
        clienteEmail: form.clienteEmail,
        valorCents,
      });
      setForm({ descricao: '', clienteNome: '', clienteEmail: '', valor: '' });
      await load();
    } catch {
      setAlert('Não foi possível gerar a cobrança. Confira o PagBank.');
    }
  };

  const cancel = async (id: string) => {
    try {
      await api.post(`/cobrancas/${id}/cancelar`);
      await load();
    } catch {
      setAlert('Não foi possível cancelar a cobrança.');
    }
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography sx={{ fontWeight: 800, mb: 1 }}>Nova cobrança</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={formRowSx}>
          <TextField size="small" label="Descrição" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
          <TextField size="small" label="Cliente" value={form.clienteNome} onChange={(e) => setForm({ ...form, clienteNome: e.target.value })} />
          <TextField size="small" label="E-mail" value={form.clienteEmail} onChange={(e) => setForm({ ...form, clienteEmail: e.target.value })} />
          <TextField size="small" label="Valor" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} />
          <Button variant="contained" onClick={() => void create()} sx={{ ...fieldButtonSx, minWidth: 128 }}>Gerar link</Button>
        </Stack>
      </Paper>
      {rows.length === 0 ? (
        <Typography color="text.secondary">Nenhuma cobrança neste sistema ainda.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Descrição</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell>Link</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {page.slice.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.descricao}</TableCell>
                <TableCell>{row.clienteNome}</TableCell>
                <TableCell><StatusChip label={row.status} /></TableCell>
                <TableCell align="right">{money(row.valorCents)}</TableCell>
                <TableCell>{row.linkUrl ? <a href={row.linkUrl}>Abrir</a> : '—'}</TableCell>
                <TableCell align="right">
                  <Button size="small" variant="outlined" disabled={row.status !== 'pendente'} onClick={() => void cancel(row.id)}>
                    Cancelar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {page.pager}
      <PanelError message={alert} onClose={() => setAlert('')} />
    </Stack>
  );
};

interface MensalRow {
  competencia: string;
  cogsCents: number;
  vendasMarketingCents: number;
  saldoCaixaCents: number;
  netCashBurnCents: number;
}

const MensalPanel: React.FC = () => {
  const [rows, setRows] = useState<MensalRow[]>([]);
  const [alert, setAlert] = useState('');
  const [form, setForm] = useState({ competencia: new Date().toISOString().slice(0, 7), cogs: '', vendas: '', caixa: '', burn: '' });
  const page = useClientPage(rows);
  const reais = (value: number) => (value / 100).toFixed(2).replace('.', ',');

  const load = useCallback(async () => {
    try {
      const res = await api.get<MensalRow[]>('/financeiro-mensal');
      setRows(res.data);
    } catch {
      setAlert('Não foi possível carregar a planilha mensal.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    const cogsCents = cents(form.cogs) ?? 0;
    const vendasMarketingCents = cents(form.vendas) ?? 0;
    const saldoCaixaCents = cents(form.caixa) ?? 0;
    const netCashBurnCents = cents(form.burn) ?? 0;
    try {
      await api.put(`/financeiro-mensal/${form.competencia}`, {
        cogsCents,
        vendasMarketingCents,
        saldoCaixaCents,
        netCashBurnCents,
      });
      await load();
    } catch {
      setAlert('Não foi possível salvar a competência.');
    }
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={formRowSx}>
          <CompetenceMonthField size="small" label="Competência" value={form.competencia} onChange={(next) => setForm({ ...form, competencia: next })} minWidth={200} />
          <TextField size="small" label="COGS" value={form.cogs} onChange={(e) => setForm({ ...form, cogs: e.target.value })} />
          <TextField size="small" label="Vendas e marketing" value={form.vendas} onChange={(e) => setForm({ ...form, vendas: e.target.value })} />
          <TextField size="small" label="Saldo de caixa" value={form.caixa} onChange={(e) => setForm({ ...form, caixa: e.target.value })} />
          <TextField size="small" label="Net cash burn" value={form.burn} onChange={(e) => setForm({ ...form, burn: e.target.value })} />
          <Button variant="contained" onClick={() => void save()} sx={fieldButtonSx}>Salvar</Button>
        </Stack>
      </Paper>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Mês</TableCell>
            <TableCell align="right">COGS</TableCell>
            <TableCell align="right">Vendas e marketing</TableCell>
            <TableCell align="right">Caixa</TableCell>
            <TableCell align="right">Net cash burn</TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {page.slice.map((row) => (
            <TableRow key={row.competencia}>
              <TableCell>{String(row.competencia).slice(0, 7)}</TableCell>
              <TableCell align="right">{money(row.cogsCents)}</TableCell>
              <TableCell align="right">{money(row.vendasMarketingCents)}</TableCell>
              <TableCell align="right">{money(row.saldoCaixaCents)}</TableCell>
              <TableCell align="right">{money(row.netCashBurnCents)}</TableCell>
              <TableCell align="right">
                <Button
                  size="small"
                  onClick={() =>
                    setForm({
                      competencia: String(row.competencia).slice(0, 7),
                      cogs: reais(row.cogsCents),
                      vendas: reais(row.vendasMarketingCents),
                      caixa: reais(row.saldoCaixaCents),
                      burn: reais(row.netCashBurnCents),
                    })
                  }
                >
                  Editar
                </Button>
                <Button
                  size="small"
                  onClick={async () => {
                    try {
                      await api.post(`/financeiro-mensal/${String(row.competencia).slice(0, 7)}/ressincronizar`);
                      await load();
                    } catch {
                      setAlert('Não foi possível reenviar ao board.');
                    }
                  }}
                >
                  Reenviar
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {page.pager}
      <PanelError message={alert} onClose={() => setAlert('')} />
    </Stack>
  );
};

interface Banda {
  canal: 'meta' | 'google' | 'linkedin';
  maxDiarioCents: number;
  ativo: boolean;
}

const BANDAS: Banda['canal'][] = ['meta', 'google', 'linkedin'];

const BandasPanel: React.FC = () => {
  const [rows, setRows] = useState<Banda[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState('');
  const page = useClientPage([...BANDAS]);

  const load = useCallback(async () => {
    try {
      const res = await api.get<Banda[]>('/bandas-orcamento');
      setRows(res.data);
      setDrafts(Object.fromEntries(res.data.map((row) => [row.canal, (row.maxDiarioCents / 100).toFixed(2).replace('.', ',')])));
    } catch {
      setAlert('Não foi possível carregar as bandas.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (canal: Banda['canal'], ativo: boolean) => {
    const maxDiarioCents = cents(drafts[canal] || '0');
    if (maxDiarioCents == null || maxDiarioCents < 0) {
      setAlert('Informe o teto diário.');
      return;
    }
    try {
      await api.put(`/bandas-orcamento/${canal}`, { maxDiarioCents, ativo });
      await load();
    } catch {
      setAlert('Não foi possível salvar a banda.');
    }
  };

  return (
    <Grid container spacing={2}>
      {page.slice.map((canal) => {
        const row = rows.find((item) => item.canal === canal);
        return (
          <Grid key={canal} size={{ xs: 12, md: 4 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography sx={{ fontWeight: 800, textTransform: 'capitalize', mb: 1 }}>{canal}</Typography>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-end', mb: 1.5 }}>
                <TextField
                  size="small"
                  label="Teto diário"
                  fullWidth
                  value={drafts[canal] ?? ''}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [canal]: e.target.value }))}
                />
                <Button variant="contained" onClick={() => void save(canal, Boolean(row?.ativo))} sx={fieldButtonSx}>
                  Salvar
                </Button>
              </Stack>
              <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(row?.ativo)}
                      onChange={(_event, checked) => void save(canal, checked)}
                      slotProps={{ input: { 'aria-label': `${canal} ${row?.ativo ? 'ativo' : 'bloqueado'}` } }}
                    />
                  }
                  label={row?.ativo ? 'Ativado' : 'Bloqueado'}
                />
              </Stack>
            </Paper>
          </Grid>
        );
      })}
      {page.pager}
      <PanelError message={alert} onClose={() => setAlert('')} />
    </Grid>
  );
};

interface PayrollSheet {
  id: string;
  title?: string | null;
  referenceMonth: string;
  cycleName?: string | null;
  lines?: Array<{ id: string; description: string; amount: number | string; bucket: string }>;
}

const FolhaPanel: React.FC = () => {
  const [sheets, setSheets] = useState<PayrollSheet[]>([]);
  const [alert, setAlert] = useState('');
  const year = new Date().getFullYear();
  const page = useClientPage(sheets);
  const [cycles, setCycles] = useState<Array<{ id: string; name: string }>>([]);
  const [sheetForm, setSheetForm] = useState({ cycleId: '', title: '' });
  const [lineForm, setLineForm] = useState({ sheetId: '', bucket: 'salaries', description: '', amount: '' });

  const removeSheet = async (id: string) => {
    try {
      await api.delete(`/payroll/sheets/${id}`);
      await load();
    } catch {
      setAlert('Não foi possível excluir a folha.');
    }
  };

  const load = useCallback(async () => {
    try {
      const res = await api.get<PayrollSheet[]>('/payroll/sheets', { params: { year } });
      setSheets(res.data);
    } catch {
      setAlert('Não foi possível carregar a folha.');
    }
  }, [year]);

  useEffect(() => {
    void load();
    void api
      .get<Array<{ id: string; name: string }>>('/finance/cycles')
      .then((res) => setCycles(res.data))
      .catch(() => undefined);
  }, [load]);

  const createSheet = async () => {
    try {
      await api.post('/payroll/sheets', {
        cycleId: sheetForm.cycleId || undefined,
        title: sheetForm.title || undefined,
        referenceMonth: sheetForm.cycleId ? undefined : `${year}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`,
        copyFromPreviousMonth: true,
      });
      setSheetForm({ cycleId: '', title: '' });
      await load();
    } catch {
      setAlert('Não foi possível criar a folha.');
    }
  };

  const addLine = async (sheetId: string) => {
    const amount = Number(String(lineForm.amount).replace(/\./g, '').replace(',', '.'));
    if (!lineForm.description.trim() || !Number.isFinite(amount)) {
      setAlert('Informe descrição e valor da linha.');
      return;
    }
    try {
      await api.post(`/payroll/sheets/${sheetId}/lines`, {
        bucket: lineForm.bucket,
        description: lineForm.description.trim(),
        amount,
      });
      setLineForm({ sheetId: '', bucket: 'salaries', description: '', amount: '' });
      await load();
    } catch {
      setAlert('Não foi possível incluir a linha.');
    }
  };

  const payslip = async (lineId: string) => {
    try {
      const res = await api.get(`/payroll/lines/${lineId}/payslip`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'contracheque.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch {
      setAlert('Não foi possível baixar o contracheque.');
    }
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={formRowSx}>
          <TextField select size="small" label="Ciclo" value={sheetForm.cycleId} onChange={(e) => setSheetForm({ ...sheetForm, cycleId: e.target.value })} slotProps={{ select: { native: true } }} sx={{ minWidth: 200 }}>
            <option value="">Mês civil</option>
            {cycles.map((cycle) => (
              <option key={cycle.id} value={cycle.id}>{cycle.name}</option>
            ))}
          </TextField>
          <TextField size="small" label="Título" value={sheetForm.title} onChange={(e) => setSheetForm({ ...sheetForm, title: e.target.value })} />
          <Button variant="contained" onClick={() => void createSheet()} sx={fieldButtonSx}>Criar folha</Button>
        </Stack>
      </Paper>
      {page.slice.map((sheet) => (
        <Paper key={sheet.id} variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontWeight: 800 }}>{sheet.title || sheet.cycleName || String(sheet.referenceMonth).slice(0, 10)}</Typography>
            <Button size="small" color="inherit" onClick={() => void removeSheet(sheet.id)}>Excluir</Button>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ ...formRowSx, mb: 1 }}>
            <TextField select size="small" label="Grupo" value={lineForm.sheetId === sheet.id ? lineForm.bucket : 'salaries'} onChange={(e) => setLineForm({ ...lineForm, sheetId: sheet.id, bucket: e.target.value })} slotProps={{ select: { native: true } }}>
              <option value="salaries">Salários</option>
              <option value="withholdings">Descontos</option>
              <option value="thirteenth">13º</option>
              <option value="vacation">Férias</option>
              <option value="other">Outros</option>
            </TextField>
            <TextField size="small" label="Descrição" value={lineForm.sheetId === sheet.id ? lineForm.description : ''} onChange={(e) => setLineForm({ ...lineForm, sheetId: sheet.id, description: e.target.value })} />
            <TextField size="small" label="Valor" value={lineForm.sheetId === sheet.id ? lineForm.amount : ''} onChange={(e) => setLineForm({ ...lineForm, sheetId: sheet.id, amount: e.target.value })} />
            <Button variant="contained" onClick={() => void addLine(sheet.id)} sx={fieldButtonSx}>Incluir linha</Button>
          </Stack>
          {(sheet.lines || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">Sem linhas nesta folha.</Typography>
          ) : (
            (sheet.lines || []).map((line) => (
              <Stack key={line.id} direction="row" spacing={1} sx={{ alignItems: 'center', py: 0.5 }}>
                <Typography variant="body2" sx={{ flex: 1 }}>{line.bucket} · {line.description} · {brl.format(Number(line.amount))}</Typography>
                <Button size="small" onClick={() => void payslip(line.id)}>Contracheque</Button>
                <Button size="small" color="inherit" onClick={async () => { await api.delete(`/payroll/lines/${line.id}`); await load(); }}>Excluir</Button>
              </Stack>
            ))
          )}
        </Paper>
      ))}
      {page.pager}
      <PanelError message={alert} onClose={() => setAlert('')} />
    </Stack>
  );
};

interface FinanceCycleRow {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  active: boolean;
}

const CiclosPanel: React.FC = () => {
  const [rows, setRows] = useState<FinanceCycleRow[]>([]);
  const [alert, setAlert] = useState('');
  const [form, setForm] = useState({ name: '', startDate: '', endDate: '' });
  const [editingId, setEditingId] = useState<string | null>(null);
  const page = useClientPage(rows);

  const load = useCallback(async () => {
    try {
      const res = await api.get<FinanceCycleRow[]>('/finance/cycles');
      setRows(res.data);
    } catch {
      setAlert('Não foi possível carregar os ciclos financeiros.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    if (!form.name.trim() || !form.startDate || !form.endDate) {
      setAlert('Informe nome e período.');
      return;
    }
    try {
      await api.post('/finance/cycles', form);
      setForm({ name: '', startDate: '', endDate: '' });
      setEditingId(null);
      await load();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setAlert(typeof message === 'string' ? message : 'Não foi possível criar o ciclo.');
    }
  };

  const saveEdit = async () => {
    if (!editingId) return;
    try {
      await api.patch(`/finance/cycles/${editingId}`, form);
      setEditingId(null);
      setForm({ name: '', startDate: '', endDate: '' });
      await load();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setAlert(typeof message === 'string' ? message : 'Não foi possível atualizar o ciclo.');
    }
  };

  const remove = async (id: string) => {
    try {
      await api.delete(`/finance/cycles/${id}`);
      await load();
    } catch {
      setAlert('Não foi possível excluir o ciclo.');
    }
  };

  const dateLabel = (value: string) => String(value).slice(0, 10).split('-').reverse().join('/');
  const todayKey = new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const situation = (row: FinanceCycleRow): { label: string; tone: 'success' | 'neutral' } => {
    if (row.active) return { label: 'Ciclo atual', tone: 'success' };
    if (String(row.endDate).slice(0, 10) < todayKey) return { label: 'Encerrado', tone: 'neutral' };
    return { label: 'Agendado', tone: 'neutral' };
  };

  return (
    <Stack spacing={2}>
      <Typography color="text.secondary">
        O ciclo atual segue o calendário, como no feedback. Quando o mês do próximo ciclo começa, este encerra e o novo assume. Não altera os ciclos de avaliação.
      </Typography>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={formRowSx}>
          <TextField size="small" label="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField size="small" type="date" label="Início" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField size="small" type="date" label="Fim" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
          <Button variant="contained" onClick={() => void (editingId ? saveEdit() : create())} sx={{ ...fieldButtonSx, minWidth: 132 }}>
            {editingId ? 'Atualizar' : 'Criar ciclo'}
          </Button>
        </Stack>
      </Paper>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Nome</TableCell>
            <TableCell>Início</TableCell>
            <TableCell>Fim</TableCell>
            <TableCell>Situação</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {page.slice.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell sx={{ fontWeight: 700 }}>{row.name}</TableCell>
              <TableCell>{dateLabel(row.startDate)}</TableCell>
              <TableCell>{dateLabel(row.endDate)}</TableCell>
              <TableCell>
                <StatusChip label={situation(row).label} tone={situation(row).tone} />
              </TableCell>
              <TableCell align="right">
                <Button
                  size="small"
                  onClick={() => {
                    setEditingId(row.id);
                    setForm({
                      name: row.name,
                      startDate: String(row.startDate).slice(0, 10),
                      endDate: String(row.endDate).slice(0, 10),
                    });
                  }}
                >
                  Editar
                </Button>
                <IconButton aria-label={`Excluir ciclo ${row.name}`} size="small" onClick={() => void remove(row.id)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {page.pager}
      <PanelError message={alert} onClose={() => setAlert('')} />
    </Stack>
  );
};

interface FinanceReport {
  summary: { income: number; expense: number; balance: number; count: number };
  byCategory: Array<{ label: string; income: number; expense: number }>;
  options: {
    cycles: Array<{ id: string; name: string }>;
    accounts: Array<{ id: string; name: string }>;
    categories: Array<{ id: string; name: string }>;
    sources: Array<{ id: string; name: string }>;
  };
  transactions: Array<{
    id: string;
    transactionDate: string;
    description: string;
    accountName: string;
    categoryName: string;
    cycleName: string;
    type: string;
    amount: number;
  }>;
}

const RelatoriosPanel: React.FC = () => {
  const [data, setData] = useState<FinanceReport | null>(null);
  const [alert, setAlert] = useState('');
  const [filters, setFilters] = useState({ startDate: '', endDate: '', cycleId: '', accountId: '', categoryId: '', sourceId: '', type: 'all', search: '' });
  const categoriesPage = useClientPage(data?.byCategory ?? []);
  const txPage = useClientPage(data?.transactions ?? []);

  const load = useCallback(async () => {
    try {
      const res = await api.get<FinanceReport>('/finance/reports', { params: filters });
      setData(res.data);
    } catch {
      setAlert('Não foi possível montar o relatório.');
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  const download = async (format: 'xlsx' | 'pdf') => {
    try {
      const res = await api.get(`/finance/reports/${format}`, { params: filters, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio-financeiro.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch {
      setAlert(format === 'pdf' ? 'Não foi possível baixar o PDF.' : 'Não foi possível baixar o Excel.');
    }
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ alignItems: { md: 'flex-end' }, flexWrap: 'wrap' }}>
            <TextField size="small" type="date" label="De" value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} sx={{ width: { xs: '100%', md: 160 } }} />
            <TextField size="small" type="date" label="Até" value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} sx={{ width: { xs: '100%', md: 160 } }} />
            <TextField select size="small" label="Ciclo" value={filters.cycleId} onChange={(e) => setFilters({ ...filters, cycleId: e.target.value })} slotProps={{ select: { native: true } }} sx={{ width: { xs: '100%', md: 200 } }}>
              <option value="">Todos</option>
              {(data?.options.cycles || []).map((cycle) => (
                <option key={cycle.id} value={cycle.id}>{cycle.name}</option>
              ))}
            </TextField>
            <TextField select size="small" label="Conta" value={filters.accountId} onChange={(e) => setFilters({ ...filters, accountId: e.target.value })} slotProps={{ select: { native: true } }} sx={{ width: { xs: '100%', md: 180 } }}>
              <option value="">Todas</option>
              {(data?.options.accounts || []).map((account) => (
                <option key={account.id} value={account.id}>{account.name}</option>
              ))}
            </TextField>
            <TextField select size="small" label="Tipo" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} slotProps={{ select: { native: true } }} sx={{ width: { xs: '100%', md: 140 } }}>
              <option value="all">Todos</option>
              <option value="income">Entrada</option>
              <option value="expense">Saída</option>
            </TextField>
            <TextField select size="small" label="Categoria" value={filters.categoryId} onChange={(e) => setFilters({ ...filters, categoryId: e.target.value })} slotProps={{ select: { native: true } }} sx={{ width: { xs: '100%', md: 180 } }}>
              <option value="">Todas</option>
              {(data?.options.categories || []).map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </TextField>
            <TextField select size="small" label="Fonte" value={filters.sourceId} onChange={(e) => setFilters({ ...filters, sourceId: e.target.value })} slotProps={{ select: { native: true } }} sx={{ width: { xs: '100%', md: 180 } }}>
              <option value="">Todas</option>
              {(data?.options.sources || []).map((source) => (
                <option key={source.id} value={source.id}>{source.name}</option>
              ))}
            </TextField>
            <TextField size="small" label="Busca" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} sx={{ width: { xs: '100%', md: 'auto' }, flex: { md: '1 1 220px' }, minWidth: { md: 220 } }} />
          </Stack>
          <Stack direction="row" spacing={1} sx={{ justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
            <Button variant="contained" onClick={() => void download('xlsx')} sx={{ height: 40, flex: { xs: 1, sm: '0 0 160px' } }}>
              Baixar Excel
            </Button>
            <Button variant="outlined" onClick={() => void download('pdf')} sx={{ height: 40, flex: { xs: 1, sm: '0 0 160px' } }}>
              Baixar PDF
            </Button>
          </Stack>
        </Stack>
      </Paper>
      {data ? (
        <>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">Entradas</Typography>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{brl.format(data.summary.income)}</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">Saídas</Typography>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{brl.format(data.summary.expense)}</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">Saldo · {data.summary.count} lançamentos</Typography>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{brl.format(data.summary.balance)}</Typography>
              </Paper>
            </Grid>
          </Grid>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Categoria</TableCell>
                <TableCell align="right">Entradas</TableCell>
                <TableCell align="right">Saídas</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categoriesPage.slice.map((row) => (
                <TableRow key={row.label}>
                  <TableCell>{row.label}</TableCell>
                  <TableCell align="right">{brl.format(row.income)}</TableCell>
                  <TableCell align="right">{brl.format(row.expense)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {categoriesPage.pager}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Ciclo</TableCell>
                <TableCell>Conta</TableCell>
                <TableCell align="right">Valor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {txPage.slice.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.transactionDate.split('-').reverse().join('/')}</TableCell>
                  <TableCell>{row.description}</TableCell>
                  <TableCell>{row.cycleName}</TableCell>
                  <TableCell>{row.accountName}</TableCell>
                  <TableCell align="right">{brl.format(row.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {txPage.pager}
        </>
      ) : (
        <CircularProgress />
      )}
      <PanelError message={alert} onClose={() => setAlert('')} />
    </Stack>
  );
};

export default FinanceWorkspace;
